import type { ColDef, ValueFormatterParams, ICellRendererParams, ValueGetterParams } from 'ag-grid-enterprise'
import type { ReactElement } from 'react'
import type { Menu } from '~/components/ActionMenu'
import type { IIap } from '~/types/iap'

import ActionMenu from '~/components/ActionMenu'
import IapTypeCellRenderer from '~/components/CellRenderers/IAPTypeCellRenderer'
import { FormatTextCellRenderer } from '~/components/CellRenderers/TextCellRenderer'
import WithSubTextCellRenderer from '~/components/CellRenderers/WithSubTextCellRenderer'
import sharedHeaderTemplate from '~/components/HeaderTemplate'
import { copyText } from '~/utils/navigator'
import { getNumberWithSpaces } from '~/utils/number'
import { actionsColDef, iapStatusColDef, withSubTextColDef } from '~/utils/table'

const ActionsCellRenderer = ({ data }: ICellRendererParams<IIap>): ReactElement => {
  if (data) {
    const menu: Menu = {
      actions: [
        {
          action: () => copyText(data.sku, 'Sku copied to clipboard.'),
          label: 'Copy sku',
        },
      ],
    }

    if (data && data.device_type === 'ios') {
      menu.navigations = [
        {
          action: () =>
            window.open(
              `https://appstoreconnect.apple.com/apps/${data.app_store_id}/appstore/iaps/${data.store_id}`,
              '_blank'
            ),
          label: 'View In-app on AppStoreConnect',
          type: 'external',
        },
      ]
    }

    return <ActionMenu menu={menu} placement="top" />
  }

  return <div />
}

const columns: ColDef[] = [
  withSubTextColDef(WithSubTextCellRenderer, {
    comparator: (a: { text: string }, b: { text: string }): number => {
      if (!a.text?.length) return 1
      if (!b.text?.length) return -1

      return a.text.toLowerCase().localeCompare(b.text.toLowerCase())
    },
    field: 'sku',
    filter: 'agTextColumnFilter',
    headerComponentParams: {
      menuIcon: 'fa-bars',
      template: sharedHeaderTemplate({
        headerSubtitle: 'Name',
        headerSubtitleClassname: 'capitalized',
        headerTitle: 'In-App Purchase ID',
      }),
    },
    headerName: '',
    menuTabs: ['filterMenuTab'],
    tooltipComponent: 'SkuTooltip',
    tooltipField: 'sku',
    valueGetter: ({ data }: ValueGetterParams<IIap>) => ({
      subText: data?.sku,
      text: data?.title,
    }),
  }),
  {
    field: 'sku',
    hide: true,
  },
  {
    field: 'title',
    hide: true,
  },
  {
    cellRenderer: IapTypeCellRenderer,
    field: 'type',
    headerName: 'Type',
  },
  iapStatusColDef({
    field: 'status',
  }),
  {
    cellRenderer: FormatTextCellRenderer,
    field: 'price',
    filter: 'agNumberColumnFilter',
    filterParams: {
      allowedCharPattern: '\\d\\-\\,',
      numberParser: (text: string | null): number | null => {
        return text == null ? null : parseFloat(text.replace(',', '.'))
      },
    },
    headerName: 'Price',
    maxWidth: 130,
    menuTabs: ['filterMenuTab'],
    minWidth: 90,
    valueFormatter: (params: ValueFormatterParams<IIap>): string => {
      return params.value ? `$ ${getNumberWithSpaces(params.value)}` : '--'
    },
  },
  actionsColDef(ActionsCellRenderer, {
    field: 'actions',
  }),
]

export default columns
